import { supabase } from '../supabaseClient';
import type {
  AdminEvaluationPlanCategory,
  EvaluationLevelStepCount,
  EvaluationPlanCategoryBundle,
  EvaluationPlanDraft,
  EvaluationPlanWorkspaceDraft,
  EvaluationStandardDraft,
} from '../types/evaluationPlan';
import { clampLevelStepCount, levelDisplayLabelsToDbPayload, parseEvaluationLevelDisplayLabels, parseEvaluationPlanAttachment } from '../types/evaluationPlan';
import type { EvaluationLevelDisplayLabels } from '../types/evaluationPlan';
import {
  clearWorkspaceEvaluationPlanBundleCache,
  hydrateWorkspaceEvaluationPlanBundleCache,
  loadWorkspaceEvaluationPlanBundleCache,
  loadWorkspaceEvaluationPlanCategoriesCache,
  patchWorkspaceEvaluationPlanBundleCache,
  patchWorkspaceEvaluationPlanCategoriesCache,
  renameWorkspaceEvaluationPlanBundleCache,
  saveWorkspaceEvaluationPlanBundleCache,
  saveWorkspaceEvaluationPlanCategoriesCache,
  type CachedWorkspaceEvaluationPlanCategory,
} from '../utils/workspaceEvaluationPlanCache';
import { recordDevLocalStorageProgramCacheUse } from '../utils/devNetworkTransferMonitor';
import {
  buildEvaluationPlanBundleVersion,
  buildEvaluationPlanBundleVersionFromHeads,
  evaluationPlanScopeVersionFromHead,
  isSameEvaluationPlanBundleVersion,
} from '../utils/evaluationPlanCacheVersion';
import { buildLearningContentScopeVersion } from '../utils/learningContentCacheVersion';
import {
  isWorkspaceEvaluationPlanBundleSessionDbSynced,
  isWorkspaceEvaluationPlanCategoriesSessionDbSynced,
  markWorkspaceEvaluationPlanBundleSessionDbSynced,
  markWorkspaceEvaluationPlanCategoriesSessionDbSynced,
} from '../utils/workspaceEvaluationPlanSessionSync';

const DELETE_IDS_CHUNK = 200;

const CATEGORY_SELECT = [
  'id',
  'workspace_id',
  'grade_id',
  'category_name',
  'level_step_count',
  'level_label_a',
  'level_label_b',
  'level_label_c',
  'level_label_d',
  'level_label_e',
  'sort_order',
  'created_at',
  'updated_at',
].join(', ');
const STANDARD_SELECT = [
  'id',
  'workspace_id',
  'grade_id',
  'category_name',
  'track_text',
  'area_text',
  'achievement_code',
  'achievement_text',
  'level_a',
  'level_b',
  'level_c',
  'level_d',
  'level_e',
  'sort_order',
  'created_at',
  'updated_at',
].join(', ');
const PLAN_SELECT_COLUMNS = [
  'id',
  'workspace_id',
  'grade_id',
  'category_name',
  'evaluation_element_text',
  'evaluation_method_text',
  'unit_text',
  'lesson_evaluation_text',
  'level_a',
  'level_b',
  'level_c',
  'level_d',
  'level_e',
  'level_step_count',
  'criterion_rubric_attachment',
  'evaluation_sheet_attachment',
  'sort_order',
  'created_at',
  'updated_at',
] as const;

const PLAN_SELECT = PLAN_SELECT_COLUMNS.join(', ');
const PLAN_SELECT_WITHOUT_LEVEL_STEP = PLAN_SELECT_COLUMNS.filter((col) => col !== 'level_step_count').join(', ');

function isMissingPlanLevelStepColumnError(message: string): boolean {
  const m = message.toLowerCase();
  return m.includes('level_step_count') && (m.includes('column') || m.includes('schema') || m.includes('does not exist'));
}

function normalizePlanDraft(row: EvaluationPlanDraft, standardIds: string[] = []): EvaluationPlanDraft {
  const legacyStandardId =
    typeof (row as { standard_id?: unknown }).standard_id === 'string'
      ? ((row as { standard_id?: string }).standard_id as string)
      : null;
  const mergedStandardIds =
    standardIds.length > 0
      ? standardIds
      : Array.isArray(row.standard_ids)
        ? row.standard_ids
        : legacyStandardId
          ? [legacyStandardId]
          : [];
  return {
    ...row,
    category_name: row.category_name ?? '',
    standard_ids: [...mergedStandardIds],
    level_step_count: clampLevelStepCount(row.level_step_count),
    criterion_rubric_attachment: (() => {
      const parsed = parseEvaluationPlanAttachment(row.criterion_rubric_attachment);
      return parsed ? { ...parsed, slot: 'criterion' as const } : null;
    })(),
    evaluation_sheet_attachment: (() => {
      const parsed = parseEvaluationPlanAttachment(row.evaluation_sheet_attachment);
      return parsed ? { ...parsed, slot: 'sheet' as const } : null;
    })(),
  };
}

async function loadPlanStandardLinks(planIds: string[]): Promise<Map<string, string[]>> {
  const map = new Map<string, string[]>();
  if (!planIds.length) return map;
  for (let i = 0; i < planIds.length; i += DELETE_IDS_CHUNK) {
    const chunk = planIds.slice(i, i + DELETE_IDS_CHUNK);
    const { error, data } = await supabase
      .from('admin_evaluation_plan_standard_links')
      .select('plan_id, standard_id')
      .in('plan_id', chunk);
    if (error) throw new Error(error.message);
    for (const row of data ?? []) {
      const planId = String(row.plan_id);
      const list = map.get(planId) ?? [];
      list.push(String(row.standard_id));
      map.set(planId, list);
    }
  }
  return map;
}

async function syncPlanStandardLinks(plans: EvaluationPlanDraft[]): Promise<void> {
  for (const plan of plans) {
    const { error: deleteError } = await supabase
      .from('admin_evaluation_plan_standard_links')
      .delete()
      .eq('plan_id', plan.id);
    if (deleteError) throw new Error(deleteError.message);
    const standardIds = Array.from(new Set((plan.standard_ids ?? []).filter(Boolean)));
    if (!standardIds.length) continue;
    const { error: insertError } = await supabase.from('admin_evaluation_plan_standard_links').insert(
      standardIds.map((standardId) => ({
        plan_id: plan.id,
        standard_id: standardId,
      }))
    );
    if (insertError) throw new Error(insertError.message);
  }
}

async function deleteIdsInChunks(
  table: 'admin_evaluation_standards' | 'admin_evaluation_plans',
  ids: string[]
): Promise<void> {
  for (let i = 0; i < ids.length; i += DELETE_IDS_CHUNK) {
    const chunk = ids.slice(i, i + DELETE_IDS_CHUNK);
    const { error } = await supabase.from(table).delete().in('id', chunk);
    if (error) throw new Error(error.message);
  }
}

function requireWorkspaceId(workspaceId: string): string {
  const ws = workspaceId.trim();
  if (!ws) throw new Error('워크스페이스를 선택해 주세요.');
  return ws;
}

function requireGradeId(gradeId: string): string {
  const gid = gradeId.trim();
  if (!gid) throw new Error('클래스를 선택해 주세요.');
  return gid;
}

function normalizeWorkspaceCategoryRow(
  row: AdminEvaluationPlanCategory,
  workspaceId: string
): AdminEvaluationPlanCategory {
  const labels = parseEvaluationLevelDisplayLabels(row);
  return {
    ...row,
    workspace_id: workspaceId,
    level_step_count: clampLevelStepCount(row.level_step_count),
    ...labels,
  };
}

function workspaceCategoryRowToCache(row: AdminEvaluationPlanCategory): CachedWorkspaceEvaluationPlanCategory {
  const labels = levelDisplayLabelsToDbPayload(parseEvaluationLevelDisplayLabels(row));
  return {
    id: row.id,
    category_name: row.category_name.trim(),
    sort_order: row.sort_order,
    level_step_count: row.level_step_count,
    updated_at: row.updated_at ?? '',
    ...labels,
  };
}

function cachedWorkspaceCategoriesToRows(
  workspaceId: string,
  categories: CachedWorkspaceEvaluationPlanCategory[]
): AdminEvaluationPlanCategory[] {
  return categories.map((row) =>
    normalizeWorkspaceCategoryRow(
      {
        id: row.id,
        workspace_id: workspaceId,
        category_name: row.category_name,
        level_step_count: clampLevelStepCount(row.level_step_count),
        level_label_a: row.level_label_a ?? '',
        level_label_b: row.level_label_b ?? '',
        level_label_c: row.level_label_c ?? '',
        level_label_d: row.level_label_d ?? '',
        level_label_e: row.level_label_e ?? '',
        sort_order: row.sort_order,
        created_at: '',
        updated_at: row.updated_at ?? '',
      },
      workspaceId
    )
  );
}

async function fetchWorkspaceScopedTableHead(
  table: 'admin_evaluation_plan_categories' | 'admin_evaluation_standards' | 'admin_evaluation_plans',
  workspaceId: string,
  gradeId: string,
  categoryName?: string
): Promise<string> {
  let countQuery = supabase
    .from(table)
    .select('id', { count: 'exact', head: true })
    .eq('workspace_id', workspaceId)
    .eq('grade_id', gradeId);
  if (categoryName) countQuery = countQuery.eq('category_name', categoryName);
  const { count, error: countError } = await countQuery;
  if (countError) throw new Error(countError.message);
  if (!count) return evaluationPlanScopeVersionFromHead(0, '');

  let maxQuery = supabase
    .from(table)
    .select('updated_at')
    .eq('workspace_id', workspaceId)
    .eq('grade_id', gradeId)
    .order('updated_at', { ascending: false })
    .limit(1);
  if (categoryName) maxQuery = maxQuery.eq('category_name', categoryName);
  const { data, error } = await maxQuery;
  if (error) throw new Error(error.message);
  const maxUpdatedAt = (data?.[0] as { updated_at?: string } | undefined)?.updated_at ?? '';
  return evaluationPlanScopeVersionFromHead(count, maxUpdatedAt);
}

async function fetchWorkspaceEvaluationPlanBundleHead(
  workspaceId: string,
  gradeId: string,
  categoryName: string
): Promise<string> {
  const name = categoryName.trim();
  const [standardsVersion, plansVersion] = await Promise.all([
    fetchWorkspaceScopedTableHead('admin_evaluation_standards', workspaceId, gradeId, name),
    fetchWorkspaceScopedTableHead('admin_evaluation_plans', workspaceId, gradeId, name),
  ]);
  return buildEvaluationPlanBundleVersionFromHeads({ standardsVersion, plansVersion });
}

/**
 * 학교(워크스페이스) 평가계획 카테고리.
 * LS 캐시가 있으면 head(`count:max(updated_at)`) 비교 후 동일하면 풀 fetch를 생략한다.
 */
export async function getWorkspaceEvaluationPlanCategories(
  workspaceId: string,
  gradeId: string,
  options?: { forceRefresh?: boolean }
): Promise<AdminEvaluationPlanCategory[]> {
  const ws = requireWorkspaceId(workspaceId);
  const gid = requireGradeId(gradeId);
  const cached = loadWorkspaceEvaluationPlanCategoriesCache(ws, gid);
  if (!options?.forceRefresh && cached.found && cached.contentVersion) {
    try {
      const headVersion = await fetchWorkspaceScopedTableHead(
        'admin_evaluation_plan_categories',
        ws,
        gid
      );
      if (cached.contentVersion === headVersion) {
        markWorkspaceEvaluationPlanCategoriesSessionDbSynced(ws, gid);
        if (process.env.NODE_ENV === 'development') {
          recordDevLocalStorageProgramCacheUse('평가계획·카테고리·LS');
        }
        return cachedWorkspaceCategoriesToRows(ws, cached.categories);
      }
    } catch {
      /* head 실패 시 풀 조회로 진행 */
    }
  } else if (
    !options?.forceRefresh &&
    cached.found &&
    isWorkspaceEvaluationPlanCategoriesSessionDbSynced(ws, gid)
  ) {
    if (process.env.NODE_ENV === 'development') {
      recordDevLocalStorageProgramCacheUse('평가계획·카테고리·LS');
    }
    return cachedWorkspaceCategoriesToRows(ws, cached.categories);
  }
  const { data, error } = await supabase
    .from('admin_evaluation_plan_categories')
    .select(CATEGORY_SELECT)
    .eq('workspace_id', ws)
    .eq('grade_id', gid)
    .order('sort_order', { ascending: true })
    .order('created_at', { ascending: true });
  if (error) throw new Error(error.message);
  const rows = ((data ?? []) as unknown as AdminEvaluationPlanCategory[]).map((row) => normalizeWorkspaceCategoryRow(row, ws));
  const cacheRows = rows.map((row) => workspaceCategoryRowToCache(row));
  saveWorkspaceEvaluationPlanCategoriesCache(ws, gid, cacheRows, {
    force: true,
    contentVersion: buildLearningContentScopeVersion(cacheRows),
  });
  markWorkspaceEvaluationPlanCategoriesSessionDbSynced(ws, gid);
  return rows;
}

export async function createWorkspaceEvaluationPlanCategory(
  workspaceId: string,
  gradeId: string,
  categoryName: string,
  levelStepCount: EvaluationLevelStepCount = 5,
  displayLabels: EvaluationLevelDisplayLabels = parseEvaluationLevelDisplayLabels(null)
): Promise<void> {
  const ws = requireWorkspaceId(workspaceId);
  const gid = requireGradeId(gradeId);
  const name = categoryName.trim();
  if (!name) throw new Error('카테고리 이름을 입력해 주세요.');
  const steps = clampLevelStepCount(levelStepCount);
  const labels = levelDisplayLabelsToDbPayload(displayLabels);
  const now = new Date().toISOString();
  const { data: maxRow } = await supabase
    .from('admin_evaluation_plan_categories')
    .select('sort_order')
    .eq('workspace_id', ws)
    .eq('grade_id', gid)
    .order('sort_order', { ascending: false })
    .limit(1)
    .maybeSingle();
  const nextOrder = typeof maxRow?.sort_order === 'number' ? maxRow.sort_order + 1 : 0;
  const { error } = await supabase.from('admin_evaluation_plan_categories').insert({
    workspace_id: ws,
    grade_id: gid,
    category_name: name,
    level_step_count: steps,
    ...labels,
    sort_order: nextOrder,
    updated_at: now,
  });
  if (error) throw new Error(error.message);
  patchWorkspaceEvaluationPlanCategoriesCache(ws, gid, (list) => {
    if (list.some((row) => row.category_name === name)) return list;
    const maxOrder = list.reduce((max, row) => Math.max(max, row.sort_order ?? 0), -1);
    return [
      ...list,
      {
        id: `local-${name}`,
        category_name: name,
        sort_order: maxOrder + 1,
        level_step_count: steps,
        ...labels,
      },
    ];
  });
  markWorkspaceEvaluationPlanCategoriesSessionDbSynced(ws, gid);
}

export async function updateWorkspaceEvaluationPlanCategorySettings(
  workspaceId: string,
  gradeId: string,
  categoryName: string,
  settings: {
    levelStepCount?: EvaluationLevelStepCount;
    displayLabels?: EvaluationLevelDisplayLabels;
  }
): Promise<void> {
  const ws = requireWorkspaceId(workspaceId);
  const gid = requireGradeId(gradeId);
  const name = categoryName.trim();
  if (!name) return;
  const patch: Record<string, string | number> = {
    updated_at: new Date().toISOString(),
  };
  if (settings.levelStepCount !== undefined) {
    patch.level_step_count = clampLevelStepCount(settings.levelStepCount);
  }
  if (settings.displayLabels) {
    Object.assign(patch, levelDisplayLabelsToDbPayload(settings.displayLabels));
  }
  const { error } = await supabase
    .from('admin_evaluation_plan_categories')
    .update(patch)
    .eq('workspace_id', ws)
    .eq('grade_id', gid)
    .eq('category_name', name);
  if (error) throw new Error(error.message);
  patchWorkspaceEvaluationPlanCategoriesCache(ws, gid, (list) =>
    list.map((row) => {
      if (row.category_name !== name) return row;
      const next = { ...row };
      if (settings.levelStepCount !== undefined) {
        next.level_step_count = clampLevelStepCount(settings.levelStepCount);
      }
      if (settings.displayLabels) {
        Object.assign(next, levelDisplayLabelsToDbPayload(settings.displayLabels));
      }
      return next;
    })
  );
  markWorkspaceEvaluationPlanCategoriesSessionDbSynced(ws, gid);
}

export async function updateWorkspaceEvaluationPlanCategoryLevelStep(
  workspaceId: string,
  gradeId: string,
  categoryName: string,
  levelStepCount: EvaluationLevelStepCount
): Promise<void> {
  await updateWorkspaceEvaluationPlanCategorySettings(workspaceId, gradeId, categoryName, { levelStepCount });
}

export async function renameWorkspaceEvaluationPlanCategory(
  workspaceId: string,
  gradeId: string,
  fromName: string,
  toName: string
): Promise<void> {
  const ws = requireWorkspaceId(workspaceId);
  const gid = requireGradeId(gradeId);
  const from = fromName.trim();
  const to = toName.trim();
  if (!from || !to || from === to) return;
  const now = new Date().toISOString();
  const { data: duplicate } = await supabase
    .from('admin_evaluation_plan_categories')
    .select('id')
    .eq('workspace_id', ws)
    .eq('grade_id', gid)
    .eq('category_name', to)
    .maybeSingle();
  if (duplicate?.id) throw new Error('이미 같은 이름의 카테고리가 있습니다.');
  const { error: catError } = await supabase
    .from('admin_evaluation_plan_categories')
    .update({ category_name: to, updated_at: now })
    .eq('workspace_id', ws)
    .eq('grade_id', gid)
    .eq('category_name', from);
  if (catError) throw new Error(catError.message);
  const { error: stdError } = await supabase
    .from('admin_evaluation_standards')
    .update({ category_name: to, updated_at: now })
    .eq('workspace_id', ws)
    .eq('grade_id', gid)
    .eq('category_name', from);
  if (stdError) throw new Error(stdError.message);
  const { error: planError } = await supabase
    .from('admin_evaluation_plans')
    .update({ category_name: to, updated_at: now })
    .eq('workspace_id', ws)
    .eq('grade_id', gid)
    .eq('category_name', from);
  if (planError) throw new Error(planError.message);
  patchWorkspaceEvaluationPlanCategoriesCache(ws, gid, (list) =>
    list.map((row) => (row.category_name === from ? { ...row, category_name: to } : row))
  );
  renameWorkspaceEvaluationPlanBundleCache(ws, gid, from, to);
  markWorkspaceEvaluationPlanCategoriesSessionDbSynced(ws, gid);
  markWorkspaceEvaluationPlanBundleSessionDbSynced(ws, gid, to);
}

export async function deleteWorkspaceEvaluationPlanCategory(
  workspaceId: string,
  gradeId: string,
  categoryName: string
): Promise<void> {
  const ws = requireWorkspaceId(workspaceId);
  const gid = requireGradeId(gradeId);
  const name = categoryName.trim();
  if (!name) return;
  const { data: plans, error: planFetchError } = await supabase
    .from('admin_evaluation_plans')
    .select('id')
    .eq('workspace_id', ws)
    .eq('grade_id', gid)
    .eq('category_name', name);
  if (planFetchError) throw new Error(planFetchError.message);
  const planIds = (plans ?? []).map((row) => row.id as string);
  if (planIds.length) await deleteIdsInChunks('admin_evaluation_plans', planIds);
  const { data: standards, error: stdFetchError } = await supabase
    .from('admin_evaluation_standards')
    .select('id')
    .eq('workspace_id', ws)
    .eq('grade_id', gid)
    .eq('category_name', name);
  if (stdFetchError) throw new Error(stdFetchError.message);
  const standardIds = (standards ?? []).map((row) => row.id as string);
  if (standardIds.length) await deleteIdsInChunks('admin_evaluation_standards', standardIds);
  const { error } = await supabase
    .from('admin_evaluation_plan_categories')
    .delete()
    .eq('workspace_id', ws)
    .eq('grade_id', gid)
    .eq('category_name', name);
  if (error) throw new Error(error.message);
  patchWorkspaceEvaluationPlanCategoriesCache(ws, gid, (list) => list.filter((row) => row.category_name !== name));
  clearWorkspaceEvaluationPlanBundleCache(ws, gid, name);
  markWorkspaceEvaluationPlanCategoriesSessionDbSynced(ws, gid);
}

export async function reorderWorkspaceEvaluationPlanCategories(
  workspaceId: string,
  gradeId: string,
  orderedCategoryNames: string[]
): Promise<void> {
  const ws = requireWorkspaceId(workspaceId);
  const gid = requireGradeId(gradeId);
  const names = orderedCategoryNames.map((n) => n.trim()).filter(Boolean);
  if (!names.length) return;
  const { data, error } = await supabase
    .from('admin_evaluation_plan_categories')
    .select('id, category_name, sort_order')
    .eq('workspace_id', ws)
    .eq('grade_id', gid);
  if (error) throw new Error(error.message);
  const rows = (data ?? []) as Array<{ id: string; category_name: string; sort_order: number }>;
  const byName = new Map(rows.map((row) => [row.category_name.trim(), row]));
  const allNamesInCurrentOrder = [...rows]
    .sort((a, b) => a.sort_order - b.sort_order || a.id.localeCompare(b.id))
    .map((row) => row.category_name.trim());
  const used = new Set(names);
  const mergedNames = [...names, ...allNamesInCurrentOrder.filter((n) => !used.has(n))];
  const updates: Array<{ id: string; orderIndex: number; currentOrderIndex: number }> = [];
  mergedNames.forEach((n, idx) => {
    const row = byName.get(n);
    if (!row || row.sort_order === idx) return;
    updates.push({ id: row.id, orderIndex: idx, currentOrderIndex: row.sort_order });
  });
  if (!updates.length) return;
  const now = new Date().toISOString();
  for (const update of updates) {
    const { error: updateError } = await supabase
      .from('admin_evaluation_plan_categories')
      .update({ sort_order: update.currentOrderIndex + 1000, updated_at: now })
      .eq('id', update.id);
    if (updateError) throw new Error(updateError.message);
  }
  for (const update of updates) {
    const { error: updateError } = await supabase
      .from('admin_evaluation_plan_categories')
      .update({ sort_order: update.orderIndex, updated_at: now })
      .eq('id', update.id);
    if (updateError) throw new Error(updateError.message);
  }
  patchWorkspaceEvaluationPlanCategoriesCache(ws, gid, (list) => {
    const byNameMap = new Map(list.map((row) => [row.category_name, row]));
    const next: CachedWorkspaceEvaluationPlanCategory[] = [];
    mergedNames.forEach((n, idx) => {
      const row = byNameMap.get(n);
      if (row) next.push({ ...row, sort_order: idx });
    });
    return next;
  });
  markWorkspaceEvaluationPlanCategoriesSessionDbSynced(ws, gid);
}

/**
 * 카테고리별 성취기준·평가계획 번들.
 * LS 캐시가 있으면 head 비교 후 동일하면 풀 fetch를 생략한다.
 */
export async function getWorkspaceEvaluationPlanCategoryBundle(
  workspaceId: string,
  gradeId: string,
  categoryName: string,
  options?: { forceRefresh?: boolean }
): Promise<EvaluationPlanCategoryBundle> {
  const ws = requireWorkspaceId(workspaceId);
  const gid = requireGradeId(gradeId);
  const name = categoryName.trim();
  if (!name) return { categoryName: '', standards: [], plans: [] };
  /** IndexedDB → 메모리 (IDB 불가 시 네트워크로 진행) */
  await hydrateWorkspaceEvaluationPlanBundleCache(ws, gid, name);
  const cached = loadWorkspaceEvaluationPlanBundleCache(ws, gid, name);
  if (!options?.forceRefresh && cached.found && cached.bundle && cached.contentVersion) {
    try {
      const headVersion = await fetchWorkspaceEvaluationPlanBundleHead(ws, gid, name);
      if (isSameEvaluationPlanBundleVersion(cached.contentVersion, headVersion)) {
        markWorkspaceEvaluationPlanBundleSessionDbSynced(ws, gid, name);
        if (process.env.NODE_ENV === 'development') {
          recordDevLocalStorageProgramCacheUse('평가계획·번들·LS');
        }
        return cached.bundle;
      }
    } catch {
      /* head 실패 시 풀 조회로 진행 */
    }
  } else if (
    !options?.forceRefresh &&
    cached.found &&
    cached.bundle &&
    isWorkspaceEvaluationPlanBundleSessionDbSynced(ws, gid, name)
  ) {
    if (process.env.NODE_ENV === 'development') {
      recordDevLocalStorageProgramCacheUse('평가계획·번들·LS');
    }
    return cached.bundle;
  }
  const { data: standardsData, error: standardsError } = await supabase
    .from('admin_evaluation_standards')
    .select(STANDARD_SELECT)
    .eq('workspace_id', ws)
    .eq('grade_id', gid)
    .eq('category_name', name)
    .order('sort_order', { ascending: true })
    .order('created_at', { ascending: true });
  if (standardsError) throw new Error(standardsError.message);
  const standards = ((standardsData ?? []) as unknown) as EvaluationStandardDraft[];
  let { data: plansData, error: plansError } = await supabase
    .from('admin_evaluation_plans')
    .select(PLAN_SELECT)
    .eq('workspace_id', ws)
    .eq('grade_id', gid)
    .eq('category_name', name)
    .order('sort_order', { ascending: true })
    .order('created_at', { ascending: true });
  if (plansError && isMissingPlanLevelStepColumnError(plansError.message)) {
    ({ data: plansData, error: plansError } = await supabase
      .from('admin_evaluation_plans')
      .select(PLAN_SELECT_WITHOUT_LEVEL_STEP)
      .eq('workspace_id', ws)
      .eq('grade_id', gid)
      .eq('category_name', name)
      .order('sort_order', { ascending: true })
      .order('created_at', { ascending: true }));
  }
  if (plansError) throw new Error(plansError.message);
  const rawPlans = (plansData ?? []) as unknown as EvaluationPlanDraft[];
  const planIds = rawPlans.map((row) => row.id);
  const linksByPlanId = await loadPlanStandardLinks(planIds);
  const plans = rawPlans.map((row) => normalizePlanDraft(row, linksByPlanId.get(row.id) ?? []));
  const bundle: EvaluationPlanCategoryBundle = { categoryName: name, standards, plans };
  saveWorkspaceEvaluationPlanBundleCache(ws, gid, name, bundle, {
    force: true,
    contentVersion: buildEvaluationPlanBundleVersion(bundle),
  });
  markWorkspaceEvaluationPlanBundleSessionDbSynced(ws, gid, name);
  return bundle;
}

export async function saveWorkspaceEvaluationPlanCategoryBundle(
  workspaceId: string,
  gradeId: string,
  draft: EvaluationPlanWorkspaceDraft
): Promise<EvaluationPlanCategoryBundle> {
  const ws = requireWorkspaceId(workspaceId);
  const gid = requireGradeId(gradeId);
  const categoryName = draft.categoryName.trim();
  if (!categoryName) throw new Error('카테고리를 선택해 주세요.');
  const now = new Date().toISOString();

  if (draft.deletedPlanIds.length) {
    await deleteIdsInChunks('admin_evaluation_plans', draft.deletedPlanIds);
  }
  if (draft.deletedStandardIds.length) {
    await deleteIdsInChunks('admin_evaluation_standards', draft.deletedStandardIds);
  }

  const standardUpserts = draft.standards.map((row, idx) => ({
    id: row.id,
    workspace_id: ws,
    grade_id: gid,
    category_name: categoryName,
    track_text: row.track_text ?? '',
    area_text: row.area_text ?? '',
    achievement_code: row.achievement_code ?? '',
    achievement_text: row.achievement_text ?? '',
    level_a: row.level_a ?? '',
    level_b: row.level_b ?? '',
    level_c: row.level_c ?? '',
    level_d: row.level_d ?? '',
    level_e: row.level_e ?? '',
    sort_order: row.sort_order ?? idx,
    updated_at: now,
  }));
  if (standardUpserts.length) {
    const { error } = await supabase.from('admin_evaluation_standards').upsert(standardUpserts, { onConflict: 'id' });
    if (error) throw new Error(error.message);
  }

  const planUpserts = draft.plans.map((row, idx) => ({
    id: row.id,
    workspace_id: ws,
    grade_id: gid,
    category_name: categoryName,
    evaluation_element_text: row.evaluation_element_text ?? '',
    evaluation_method_text: row.evaluation_method_text ?? '',
    unit_text: row.unit_text ?? '',
    lesson_evaluation_text: row.lesson_evaluation_text ?? '',
    level_a: row.level_a ?? '',
    level_b: row.level_b ?? '',
    level_c: row.level_c ?? '',
    level_d: row.level_d ?? '',
    level_e: row.level_e ?? '',
    level_step_count: clampLevelStepCount(row.level_step_count),
    criterion_rubric_attachment: row.criterion_rubric_attachment ?? null,
    evaluation_sheet_attachment: row.evaluation_sheet_attachment ?? null,
    sort_order: row.sort_order ?? idx,
    updated_at: now,
  }));
  if (planUpserts.length) {
    let { error } = await supabase.from('admin_evaluation_plans').upsert(planUpserts, { onConflict: 'id' });
    if (error && isMissingPlanLevelStepColumnError(error.message)) {
      const withoutLevelStep = planUpserts.map(({ level_step_count: _omit, ...rest }) => rest);
      ({ error } = await supabase.from('admin_evaluation_plans').upsert(withoutLevelStep, { onConflict: 'id' }));
    }
    if (error) throw new Error(error.message);
  }

  await syncPlanStandardLinks(draft.plans);

  const bundle: EvaluationPlanCategoryBundle = {
    categoryName,
    standards: draft.standards,
    plans: draft.plans.map((row) => normalizePlanDraft(row, row.standard_ids ?? [])),
  };
  saveWorkspaceEvaluationPlanBundleCache(ws, gid, categoryName, bundle, {
    force: true,
    contentVersion: buildEvaluationPlanBundleVersionFromHeads({
      standardsVersion: evaluationPlanScopeVersionFromHead(draft.standards.length, now),
      plansVersion: evaluationPlanScopeVersionFromHead(draft.plans.length, now),
    }),
  });
  markWorkspaceEvaluationPlanBundleSessionDbSynced(ws, gid, categoryName);
  return bundle;
}

/** 워크스페이스 평가계획 슬롯 첨부 제거 */
export async function clearWorkspaceEvaluationPlanSlotAttachment(params: {
  workspaceId: string;
  gradeId: string;
  planId: string;
  categoryName: string;
  slot: import('../types/evaluationPlan').EvaluationPlanAttachmentSlot;
}): Promise<void> {
  const ws = requireWorkspaceId(params.workspaceId);
  const gid = requireGradeId(params.gradeId);
  const planId = params.planId.trim();
  const categoryName = params.categoryName.trim();
  if (!planId || !categoryName) throw new Error('삭제할 문서 정보가 없습니다.');

  const column = params.slot === 'criterion' ? 'criterion_rubric_attachment' : 'evaluation_sheet_attachment';
  const now = new Date().toISOString();
  const { error } = await supabase
    .from('admin_evaluation_plans')
    .update({ [column]: null, updated_at: now })
    .eq('id', planId)
    .eq('workspace_id', ws)
    .eq('grade_id', gid)
    .eq('category_name', categoryName);
  if (error) throw new Error(error.message);

  patchWorkspaceEvaluationPlanBundleCache(ws, gid, categoryName, (prev) => ({
    ...prev,
    plans: prev.plans.map((plan) =>
      plan.id === planId
        ? params.slot === 'criterion'
          ? { ...plan, criterion_rubric_attachment: null }
          : { ...plan, evaluation_sheet_attachment: null }
        : plan
    ),
  }));
  markWorkspaceEvaluationPlanBundleSessionDbSynced(ws, gid, categoryName);
}

/** admin/team 카테고리에서 사용자 개인 성취기준·평가계획만 저장 */
export async function saveWorkspaceEvaluationPlanUserPlansOnly(
  workspaceId: string,
  gradeId: string,
  categoryName: string,
  draft: EvaluationPlanWorkspaceDraft,
  userPlanIds: Set<string>,
  userStandardIds: Set<string> = new Set()
): Promise<EvaluationPlanCategoryBundle> {
  const ws = requireWorkspaceId(workspaceId);
  const gid = requireGradeId(gradeId);
  const name = categoryName.trim();
  if (!name) throw new Error('카테고리를 선택해 주세요.');
  const now = new Date().toISOString();

  const userStandards = draft.standards.filter((standard) => userStandardIds.has(standard.id));
  const deletedUserStandardIds = draft.deletedStandardIds;
  const userPlans = draft.plans.filter((plan) => userPlanIds.has(plan.id));
  const deletedUserPlanIds = draft.deletedPlanIds;

  if (deletedUserStandardIds.length) {
    await deleteIdsInChunks('admin_evaluation_standards', deletedUserStandardIds);
  }
  if (deletedUserPlanIds.length) {
    await deleteIdsInChunks('admin_evaluation_plans', deletedUserPlanIds);
  }

  const standardUpserts = userStandards.map((row, idx) => ({
    id: row.id,
    workspace_id: ws,
    grade_id: gid,
    category_name: name,
    track_text: row.track_text ?? '',
    area_text: row.area_text ?? '',
    achievement_code: row.achievement_code ?? '',
    achievement_text: row.achievement_text ?? '',
    level_a: row.level_a ?? '',
    level_b: row.level_b ?? '',
    level_c: row.level_c ?? '',
    level_d: row.level_d ?? '',
    level_e: row.level_e ?? '',
    sort_order: row.sort_order ?? idx,
    updated_at: now,
  }));
  if (standardUpserts.length) {
    const { error } = await supabase.from('admin_evaluation_standards').upsert(standardUpserts, { onConflict: 'id' });
    if (error) throw new Error(error.message);
  }

  const planUpserts = userPlans.map((row, idx) => ({
    id: row.id,
    workspace_id: ws,
    grade_id: gid,
    category_name: name,
    evaluation_element_text: row.evaluation_element_text ?? '',
    evaluation_method_text: row.evaluation_method_text ?? '',
    unit_text: row.unit_text ?? '',
    lesson_evaluation_text: row.lesson_evaluation_text ?? '',
    level_a: row.level_a ?? '',
    level_b: row.level_b ?? '',
    level_c: row.level_c ?? '',
    level_d: row.level_d ?? '',
    level_e: row.level_e ?? '',
    level_step_count: clampLevelStepCount(row.level_step_count),
    criterion_rubric_attachment: row.criterion_rubric_attachment ?? null,
    evaluation_sheet_attachment: row.evaluation_sheet_attachment ?? null,
    sort_order: row.sort_order ?? idx,
    updated_at: now,
  }));
  if (planUpserts.length) {
    let { error } = await supabase.from('admin_evaluation_plans').upsert(planUpserts, { onConflict: 'id' });
    if (error && isMissingPlanLevelStepColumnError(error.message)) {
      const withoutLevelStep = planUpserts.map(({ level_step_count: _omit, ...rest }) => rest);
      ({ error } = await supabase.from('admin_evaluation_plans').upsert(withoutLevelStep, { onConflict: 'id' }));
    }
    if (error) throw new Error(error.message);
  }

  await syncPlanStandardLinks(userPlans);

  const userBundle = await getWorkspaceEvaluationPlanCategoryBundle(ws, gid, name, { forceRefresh: true });
  patchWorkspaceEvaluationPlanBundleCache(ws, gid, name, () => userBundle);
  return userBundle;
}
